import {
  ArrowRightStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API_URLS } from "@/config/urls";
import { useLogout } from "@/hooks/useLogout";
import { cn } from "@/lib/utils";

const getWhaleProfileUrl = () => {
  try {
    return new URL("/settings/profile", API_URLS.WHALE).toString();
  } catch {
    console.warn("Invalid Whale URL:", API_URLS.WHALE);
    return "#";
  }
};

export const UserIconMenu = ({
  displayUsername,
  profileImageUrl,
}: {
  displayUsername?: string;
  profileImageUrl?: string | null;
}) => {
  const { t } = useTranslation();
  const { logout } = useLogout();
  const [isPolicyMenuOpen, setIsPolicyMenuOpen] = useState(false);
  const [failedProfileImageUrl, setFailedProfileImageUrl] = useState<
    string | null
  >(null);
  const profileImageSrc = profileImageUrl ?? undefined;
  const shouldShowProfileImage =
    profileImageSrc && failedProfileImageUrl !== profileImageSrc;

  const onClickLogout = () => {
    void logout();
  };

  const togglePolicyMenu = () => {
    setIsPolicyMenuOpen(!isPolicyMenuOpen);
  };

  const policyLinks = [
    {
      label: t("common.termsAndConditions"),
      href: t("common.termsAndConditionsUrl"),
    },
    { label: t("common.privacyPolicy"), href: t("common.privacyPolicyUrl") },
    { label: t("common.dataPolicy"), href: t("common.dataPolicyUrl") },
  ];

  const whaleProfileUrl = getWhaleProfileUrl();

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setIsPolicyMenuOpen(false);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("common.profile")}
          className="size-9 overflow-hidden rounded-full bg-white p-0"
        >
          {shouldShowProfileImage ? (
            <img
              src={profileImageSrc}
              alt=""
              className="size-full object-cover"
              referrerPolicy="no-referrer"
              onError={() => setFailedProfileImageUrl(profileImageSrc)}
            />
          ) : (
            <User className="size-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="w-max divide-y divide-gray-100 overflow-visible rounded bg-white text-sm/6 shadow-xl"
      >
        {displayUsername && (
          <span
            className="m-1 block truncate text-gray-500 [font-size:clamp(0.65rem,2vw,0.875rem)]"
            translate="no"
          >
            {displayUsername}
          </span>
        )}
        <DropdownMenuItem asChild>
          <a
            href={whaleProfileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex w-full items-center gap-2 px-3 py-1.5 focus:outline-none data-[highlighted]:bg-gray-100"
            onClick={(event) => {
              if (whaleProfileUrl === "#") {
                event.preventDefault();
              }
            }}
          >
            <IdentificationIcon className="size-4" />
            {t("common.memberInformation")}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left focus:outline-none",
            isPolicyMenuOpen
              ? "bg-gray-100"
              : "hover:bg-gray-100 data-[highlighted]:bg-gray-100",
          )}
          onSelect={(event) => {
            event.preventDefault();
            togglePolicyMenu();
          }}
        >
          <DocumentTextIcon className="size-4" />
          <span>{t("common.termsAndPolicies")}</span>
        </DropdownMenuItem>
        {isPolicyMenuOpen &&
          policyLinks.map(({ label, href }) => (
            <DropdownMenuItem asChild key={label}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-gray-100 focus:outline-none data-[highlighted]:bg-gray-100"
              >
                <span>{label}</span>
                <ArrowTopRightOnSquareIcon className="size-3.5 text-gray-400" />
              </a>
            </DropdownMenuItem>
          ))}
        <DropdownMenuItem
          onSelect={onClickLogout}
          className="group flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 focus:outline-none data-[highlighted]:bg-gray-100"
        >
          <ArrowRightStartOnRectangleIcon className="size-4" />
          {t("common.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
